import { GQLResponseError } from '@change-corgi/core/gqlErrors';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionDetailsDiscussionData,
	type PetitionDetailsDiscussionDataQuery,
	type PetitionDetailsDiscussionDataQueryVariables,
} from './discussionData.graphql';

export type DiscussionData = NonNullable<PetitionDetailsDiscussionDataQuery['petition']>;

// TODO handle errors in the UI instead of falling back to empty data — see CHANGE-95697
const EMPTY_DISCUSSION_DATA: DiscussionData = {
	supporterCommentsConnection: {
		totalCount: 0,
		totalCountLocalized: '0',
		nodes: [],
	},
	mediaHitsConnection: {
		nodes: [],
		pageInfo: { hasNextPage: false, endCursor: null },
		totalCount: 0,
		totalCountLocalized: '0',
	},
	mediaSourcesConnection: {
		nodes: [],
		totalCount: 0,
	},
	spotlightMediaSourceUpdate: {
		nodes: [],
	},
};

const DISCUSSION_DATA_ERROR_SAMPLE_RATE = 0.01;

/**
 * Comments, media hits, media sources, and the media source spotlight update are all backed by discussion_service. Fetching them as
 * their own request (instead of as part of the main PetitionDetailsData query) means a
 * discussion_service outage only degrades this data to empty defaults, instead of failing the
 * whole page (CHANGE-95607) or changing the error-handling contract of these fields for their
 * other GraphQL consumers (petitionFeed, signedPetitionHome, starter dashboard, etc.).
 */
export async function getPetitionDiscussionData(
	utilityContext: UtilityContext,
	variables: PetitionDetailsDiscussionDataQueryVariables,
): Promise<DiscussionData> {
	try {
		const { data } = await utilityContext.gql.fetch<
			PetitionDetailsDiscussionDataQuery,
			PetitionDetailsDiscussionDataQueryVariables
		>({
			query: PetitionDetailsDiscussionData,
			variables,
			rejectOnError: true,
			batched: true,
		});

		return data.petition ?? EMPTY_DISCUSSION_DATA;
	} catch (error) {
		// reported so that an outage is distinguishable from a petition with no comments or media, and
		// sampled because every petition page render hits this path while discussion_service is down
		void utilityContext.errorReporter.child({ sampleRate: DISCUSSION_DATA_ERROR_SAMPLE_RATE }).report(
			{
				error: new Error('Failed to load petition discussion data', { cause: error }),
				context: {
					requestId: error instanceof GQLResponseError ? error.requestId : undefined,
				},
				params: {
					slugOrId: variables.slugOrId,
					...(error instanceof GQLResponseError ? { errors: error.errors } : undefined),
				},
			},
			{ severity: 'warning' },
		);

		return EMPTY_DISCUSSION_DATA;
	}
}
